// MemberEngagement — per-member health check dashboard (R10)
// Sections: KPI row, engagement scores, physical compliance, LOSAP tracking,
// meeting attendance distribution, driver qualification status.

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  useAggregates,
  useMembers,
  MEMBERS_PAGE_LIMIT,
} from "../hooks/useDashboard";
import {
  usePhysicals,
  useMeetingAttendance,
  useAtRisk,
  useLosap,
  useDriverTraining,
} from "../hooks/useForge";
import type {
  PhysicalsRow,
  MeetingAttendanceRow,
  AtRiskRow,
  LosapRow,
  DriverTrainingRow,
} from "../hooks/useForge";
import type { MemberRow } from "../hooks/useDashboard";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import AlertList from "../components/dashboard/AlertList";
import type { AlertItem } from "../components/dashboard/AlertList";
import StatusBadge from "../components/dashboard/StatusBadge";
import TruncationNotice from "../components/shared/TruncationNotice";
import Banner from "../components/shared/Banner";
import EngagementScoreCard from "../components/dashboard/EngagementScoreCard";
import type { EngagementMember } from "../components/dashboard/EngagementScoreCard";
import { useTheme } from "../hooks/useTheme";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";

// ── Constants ─────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#2a7a6c";
const GHOST_COLOR = "#dc2626";

/**
 * Statuses that count a member as active. Values are exactly as `Status_New`
 * records them in dash_members — matching is case- and punctuation-sensitive.
 * RetentionAnalysis and PipelineOverview hold the identical set.
 */
export const ACTIVE_STATUSES = new Set([
  "Running",
  "Probationary",
  "Associate (Active)",
  "Life Member (Active)",
  "Probationary (Cadet)",
  "Probationary (HS)",
  "Associate (Probationary)",
]);

/**
 * The dash_members column this page filters on, in the worker's snake_case
 * form. Verified against remote D1 2026-08-27.
 *
 * This page previously read `m.status`. **dash_members has no `status`
 * column** — it has `Status_New` and `Status_Access_Database`. The read
 * returned undefined for all 1,895 rows, `ACTIVE_STATUSES.has("")` was false
 * for every one of them, and `activeMembers` was permanently empty. Seven
 * rendered figures are computed off that array, including the expired-physical
 * alert list: the compliance surface could not raise an alert at all.
 *
 * Pinned here and asserted in MemberEngagement.test.ts so a rename fails the
 * build rather than silently emptying the page again.
 */
export const MEMBER_STATUS_KEY = "status_new" as const;
/** The dash_members display-name column. Display only — NOT a join key. */
export const MEMBER_NAME_KEY = "full_name" as const;

/**
 * ── JOIN KEYS ────────────────────────────────────────────────────────────────
 *
 * This page joins four FORGE feeds onto members. Every one of those joins was
 * written as `normalName(feedRow.member) === normalName(member.full_name)`, and
 * **every one of them matched zero rows in production.** Verified against
 * remote D1 2026-08-27 by counting the join both ways:
 *
 *   feed                    page joined on          reality              matches
 *   dash_physicals          Member vs Full_Name     Member = airtable_id  0 -> 288/291
 *   dash_meeting_attendance Member vs Full_Name     Member = airtable_id  0 -> 1358/1381
 *   dash_driver_training    Member vs Full_Name     Member = airtable_id  0 -> 363/363
 *   dash_losap              Name   vs Full_Name     Name = Full_Name_Co_ID 0 -> 290/294
 *
 * `dash_physicals.Member` holds a 17-character Airtable record id (`rec…`), not
 * a person's name — as do the Member columns on attendance and driver training.
 * `dash_losap.Name` holds the "Name (CO_ID)" composite, which is
 * `dash_members.Full_Name_Co_ID`, not `Full_Name`.
 *
 * The old fallback keys were dead in the same way and are gone, not kept "just
 * in case": `dash_physicals.CO_ID_from_Member` is a 6-char CO_ID that cannot
 * equal an airtable_id; `dash_meeting_attendance.Name_CO_ID` is a meeting label
 * beginning with a date; `dash_driver_training.Training_Request_For` is an
 * apparatus type ("Ambulance", "Engine"). A key that can never match is not a
 * safety net, it is the dead path that hides the miss.
 *
 * Record ids are matched case-SENSITIVELY (trim only). Airtable ids are base62
 * and `recAb…`/`recaB…` are different records; lowercasing them to be lenient
 * would invent collisions. The LOSAP composite is a human string and is matched
 * with normalName() like any other name.
 */
/** dash_members join key for physicals / attendance / driver training. */
export const MEMBER_ID_KEY = "airtable_id" as const;
/** dash_members join key for LOSAP. `Full_Name_Co_ID`, not `Full_Name`. */
export const MEMBER_LOSAP_KEY = "full_name_co_id" as const;

/** Trim-only key for Airtable record ids. Case is significant — see above. */
function idKey(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** A member's Airtable record id, as the FORGE feeds reference it. */
export function memberIdKey(m: MemberRow): string {
  return idKey(m[MEMBER_ID_KEY]);
}
/** A member's LOSAP key: the "Name (CO_ID)" composite, normalized. */
export function memberLosapKey(m: MemberRow): string {
  return normalName(m[MEMBER_LOSAP_KEY]);
}
/** dash_physicals -> member. `Member` is the member's airtable_id. */
export function physicalRowKey(row: PhysicalsRow): string {
  return idKey(row.member);
}
/** dash_meeting_attendance -> member. `Member` is the member's airtable_id. */
export function attendanceRowKey(row: MeetingAttendanceRow): string {
  return idKey(row.member);
}
/** dash_driver_training -> member. `Member` is the member's airtable_id. */
export function driverRowKey(row: DriverTrainingRow): string {
  return idKey(row.member);
}
/** dash_losap -> member. `Name` is the Full_Name_Co_ID composite. */
export function losapRowKey(row: LosapRow): string {
  return normalName(row.name);
}

/**
 * Read a member's status. The single place this page names the status column.
 *
 * Returns "" when the row carries no status — which then matches nothing in
 * ACTIVE_STATUSES, i.e. an unstatused member is NOT active. That is the
 * intended reading: absence of a status is not evidence of membership.
 */
export function memberStatus(m: MemberRow): string {
  return asString(m[MEMBER_STATUS_KEY]);
}

/** Read a member's display name. Single naming point, as above. */
export function memberName(m: MemberRow): string | null | undefined {
  return m[MEMBER_NAME_KEY];
}

const PHYSICAL_COLORS: Record<string, string> = {
  Current: "#22c55e",
  "Expiring Soon": "#f59e0b",
  Expired: "#ef4444",
  "No Record": "#6b7280",
};

const ATTENDANCE_BUCKETS = [
  { label: "0", min: 0, max: 1 },
  { label: "1–2", min: 1, max: 3 },
  { label: "3–5", min: 3, max: 6 },
  { label: "6–8", min: 6, max: 9 },
  { label: "9–12", min: 9, max: 13 },
];

const CURRENT_YEAR = new Date().getFullYear();

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function normalName(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase();
}

/** Days until a date string. Negative = already passed. */
function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

// ── Physical status classification ────────────────────────────────────────────

type PhysStatus = "current" | "expiring" | "expired" | "none";

export function classifyPhysical(row: PhysicalsRow | undefined): PhysStatus {
  if (!row) return "none";
  const statusRaw = asString(row.status).toLowerCase();
  if (statusRaw === "current") {
    const days = daysUntil(row.expires ?? null);
    if (days !== null && days <= 90) return "expiring";
    return "current";
  }
  if (statusRaw === "expired" || statusRaw === "inactive") return "expired";
  // Check days_until_expired if present
  const daysField = asNumber(row.days_until_expired, NaN);
  if (!isNaN(daysField)) {
    if (daysField < 0) return "expired";
    if (daysField <= 90) return "expiring";
    return "current";
  }
  // Fall back to expires date
  const days = daysUntil(row.expires ?? null);
  if (days === null) return "none";
  if (days < 0) return "expired";
  if (days <= 90) return "expiring";
  return "current";
}

// ── Engagement score computation ──────────────────────────────────────────────

/**
 * Compute composite engagement score for a member (0–100):
 * - Meeting attendance (t_2024_co_meeting_attendance, 0–12 → 0–30 pts)
 * - Physical: current=25, expiring=15, expired/none=0
 * - LOSAP qualified current year: 20 pts
 * - Training: 15 pts if not probationary OR released_for_assignment
 * - Driver approved: 10 pts
 */
export function computeEngagementScore(
  member: MemberRow,
  physStatus: PhysStatus,
  attendanceCount: number,
  losapQualified: boolean,
  driverApproved: boolean,
): number {
  const status = memberStatus(member).toLowerCase();
  const isProbationary = status.includes("probationary");

  // Meeting attendance: 0–12 meetings maps to 0–30 points
  const meetingPts = Math.round((Math.min(attendanceCount, 12) / 12) * 30);

  // Physical pts
  const physPts =
    physStatus === "current" ? 25 : physStatus === "expiring" ? 15 : 0;

  // LOSAP
  const losapPts = losapQualified ? 20 : 0;

  // Training (probationary without release = 0, otherwise 15)
  // We treat non-probationary as training complete; probationary members are incomplete
  const trainingComplete = !isProbationary;
  const trainingPts = trainingComplete ? 15 : 0;

  // Driver
  const driverPts = driverApproved ? 10 : 0;

  return meetingPts + physPts + losapPts + trainingPts + driverPts;
}

// ── LOSAP by year ─────────────────────────────────────────────────────────────

interface LosapYearRow {
  year: string;
  qualified: number;
  total: number;
  isCurrent: boolean;
}

function computeLosapByYear(rows: LosapRow[]): LosapYearRow[] {
  const byYear = new Map<string, { qualified: number; total: number }>();

  for (const row of rows) {
    const year = asString(row.year) || "Unknown";
    const status = asString(row.status).toLowerCase();
    const entry = byYear.get(year) ?? { qualified: 0, total: 0 };
    entry.total++;
    if (status === "qualified" || status === "yes" || status === "active") {
      entry.qualified++;
    }
    byYear.set(year, entry);
  }

  return Array.from(byYear.entries())
    .map(([year, counts]) => ({
      year,
      ...counts,
      isCurrent: year === String(CURRENT_YEAR),
    }))
    .filter((r) => r.year !== "Unknown")
    .sort((a, b) => a.year.localeCompare(b.year));
}

// ── Attendance histogram ──────────────────────────────────────────────────────

interface AttendanceBucket {
  label: string;
  count: number;
  isGhost: boolean;
}

function computeAttendanceHistogram(
  memberAttendanceCounts: Map<string, number>,
): AttendanceBucket[] {
  const bucketCounts = ATTENDANCE_BUCKETS.map(() => 0);

  for (const count of memberAttendanceCounts.values()) {
    const idx = ATTENDANCE_BUCKETS.findIndex(
      (b) => count >= b.min && count < b.max,
    );
    if (idx >= 0) bucketCounts[idx]++;
  }

  return ATTENDANCE_BUCKETS.map((b, i) => ({
    label: b.label,
    count: bucketCounts[i],
    isGhost: i === 0, // zero-attendance bucket
  }));
}

// ── Physical compliance pie data ──────────────────────────────────────────────

export interface PhysPieSlice {
  name: string;
  value: number;
  fill: string;
}

export function computePhysicalPie(
  activeMembers: MemberRow[],
  physByMember: Map<string, PhysicalsRow>,
): PhysPieSlice[] {
  let current = 0,
    expiring = 0,
    expired = 0,
    none = 0;

  for (const m of activeMembers) {
    const phys = physByMember.get(memberIdKey(m));
    const status = classifyPhysical(phys);
    if (status === "current") current++;
    else if (status === "expiring") expiring++;
    else if (status === "expired") expired++;
    else none++;
  }

  return [
    { name: "Current", value: current, fill: PHYSICAL_COLORS["Current"] },
    {
      name: "Expiring Soon",
      value: expiring,
      fill: PHYSICAL_COLORS["Expiring Soon"],
    },
    { name: "Expired", value: expired, fill: PHYSICAL_COLORS["Expired"] },
    { name: "No Record", value: none, fill: PHYSICAL_COLORS["No Record"] },
  ].filter((s) => s.value > 0);
}

// ── Derived figures ───────────────────────────────────────────────────────────
//
// Everything below is a pure function of the API payloads, extracted out of the
// component so each figure can be exercised against a payload that uses the
// REAL column names. They are the seven figures that rendered over an empty
// `activeMembers` while this page filtered on a column that does not exist.

/**
 * Active members. **The one filter this whole page hangs off.**
 *
 * Reads `status_new` (see MEMBER_STATUS_KEY). Live D1 2026-08-27: 151 of 1,895
 * rows match ACTIVE_STATUSES. The previous implementation read `m.status` and
 * matched 0.
 */
export function selectActiveMembers(members: MemberRow[]): MemberRow[] {
  return members.filter((m) => ACTIVE_STATUSES.has(memberStatus(m)));
}

/**
 * ── Date-range filtering: a code path that only just came alive ──────────────
 *
 * These were dead. The page filtered on `r.expires` and `r.meeting_date`, and
 * before the handlers normalized, BOTH read undefined on every row — so
 * `isAfterSince(undefined, since)` returned false and selecting any range other
 * than "all" dropped 100% of physicals and attendance. The page looked like it
 * had a working date filter and did not.
 *
 * Normalizing the wire makes this live for the first time, so it is pinned
 * here rather than left as a newly-live untested path. Live D1 2026-08-27:
 * `Meeting_Date` is ISO on 1381/1381 rows, `Expires` on 281/291 — the 10
 * exceptions are `{"error":"#ERROR"}` blobs.
 *
 * Those blobs must be screened out BEFORE the comparison, and an earlier draft
 * of this comment got that exactly backwards. `isAfterSince` is a raw string
 * `>=`, and `"{"` is ASCII 123 against `"2"` at 50 — so `{"error":"#ERROR"}`
 * compares GREATER than every ISO date and would be admitted by every range,
 * as the newest row in the feed. A test caught it. Unreadable is not in range,
 * the same way unreadable is not compliant.
 *
 * The page default is "all" (sinceDate null), which returns the array
 * untouched, so no headline figure moves unless a user narrows the range.
 */

/** True for a `YYYY-MM-DD…` prefix. The only shape safe for a string compare. */
function isIsoDate(v: unknown): v is string {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v);
}

export function filterPhysRowsByRange(
  rows: PhysicalsRow[],
  sinceDate: string | null,
): PhysicalsRow[] {
  if (!sinceDate) return rows;
  return rows.filter(
    (r) => isIsoDate(r.expires) && isAfterSince(r.expires, sinceDate),
  );
}

export function filterAttRowsByRange(
  rows: MeetingAttendanceRow[],
  sinceDate: string | null,
): MeetingAttendanceRow[] {
  if (!sinceDate) return rows;
  return rows.filter(
    (r) => isIsoDate(r.meeting_date) && isAfterSince(r.meeting_date, sinceDate),
  );
}

/** Member record id → most-recent physical row (latest `expires` wins). */
export function buildPhysByMember(
  physRows: PhysicalsRow[],
): Map<string, PhysicalsRow> {
  const map = new Map<string, PhysicalsRow>();
  // Sort by expires desc so the first write wins as "most recent".
  //
  // Unreadable dates are ranked LAST, explicitly.
  //
  // This is a HARDENING, not a repaired defect. `localeCompare` under en-US ICU
  // already ordered `{"error":"#ERROR"}` below an ISO date, so the previous
  // sort was correct here — but only because of collation, which varies by
  // locale and by ICU build and which a browser does not guarantee. Raw `>=`
  // orders them the opposite way (`{` is 123, `9` is 57). A member holding both
  // a real physical and an error blob would then be reported as having no
  // record on file. Verified against live D1 2026-08-27 that no member holds
  // both today, so this changes no current figure.
  const rank = (r: PhysicalsRow) => (isIsoDate(r.expires) ? 0 : 1);
  const sorted = [...physRows].sort(
    (a, b) =>
      rank(a) - rank(b) || (b.expires ?? "").localeCompare(a.expires ?? ""),
  );
  for (const row of sorted) {
    const key = physicalRowKey(row);
    if (key && !map.has(key)) map.set(key, row);
  }
  return map;
}

/** Member record id → count of meeting-attendance rows this period. */
export function buildMemberAttCounts(
  attRows: MeetingAttendanceRow[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of attRows) {
    const key = attendanceRowKey(row);
    if (!key) continue;
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return map;
}

/**
 * LOSAP keys qualified in `year`. Keyed by Full_Name_Co_ID (see MEMBER_LOSAP_KEY).
 *
 * `losapQualCount` on the page is this set's SIZE, which never needed the
 * member join and has therefore always been right. The per-member LOSAP award
 * inside the engagement score is what the broken key was silently zeroing.
 */
export function buildLosapQualifiedKeys(
  losapRows: LosapRow[],
  year: number,
): Set<string> {
  return losapKeysWhere(losapRows, year, (r) =>
    LOSAP_QUALIFIED_STATUSES.has(losapStatus(r)),
  );
}

/**
 * Distinct member keys for `year` whose LOSAP record carries ANY determination
 * — qualified or a genuine no. This is the denominator behind "0 of N".
 *
 * Distinct MEMBERS, not rows, and only rows with a usable join key: it has to
 * be countable the same way the numerator is, or the tile contradicts itself.
 */
export function buildLosapAssessedKeys(
  losapRows: LosapRow[],
  year: number,
): Set<string> {
  return losapKeysWhere(losapRows, year, (r) => isLosapDetermination(r.status));
}

/**
 * Shared spine for both key builders. ONE definition of "a LOSAP row for this
 * year, attributable to a member" — the tile and the section badge drifting
 * apart is exactly the defect this removes.
 */
function losapKeysWhere(
  losapRows: LosapRow[],
  year: number,
  pred: (row: LosapRow) => boolean,
): Set<string> {
  const yearStr = String(year);
  const s = new Set<string>();
  for (const row of losapRows) {
    if (asString(row.year) !== yearStr) continue;
    if (!pred(row)) continue;
    const key = losapRowKey(row);
    if (key) s.add(key);
  }
  return s;
}

/**
 * ── LOSAP: a zero we cannot stand behind ─────────────────────────────────────
 *
 * `buildLosapQualifiedKeys` returns an empty set, and the KPI card used to
 * render that as a GREEN `0` captioned "members qualified in 2026". Live D1
 * 2026-08-27 says the emptiness has TWO independent causes, and neither is
 * "nobody qualified":
 *
 *   SELECT Year, Status, COUNT(*) FROM dash_losap GROUP BY 1,2;
 *     -> 2023 | Unverified | 294        (the only row in the result)
 *   SELECT DISTINCT Status FROM dash_losap;  -> ['Unverified']
 *
 * So: no 2026 rows exist at all, AND not one record in the table carries a
 * determination. The set would be empty for ANY year. A green 0 there is
 * indistinguishable from "we checked and nobody qualified" — a claim the data
 * cannot support, on a leadership-facing tile.
 *
 * The rule: UNKNOWN is reported distinctly from a real zero, the uncertainty
 * lives in the tile VALUE rather than in a footnote nobody reads, and green
 * requires a positive count over data that could have produced one.
 */

/**
 * ── LOSAP status vocabulary: an ALLOWLIST of decisions ───────────────────────
 *
 * This was a denylist of absences ("unverified", "pending", …) and anything not
 * on it read as a determination. That FAILS OPEN, and it fails open against the
 * exact thing this file exists to prevent. Probed: `Not Verified`,
 * `Not verified yet`, `Awaiting Review`, `Incomplete`, `Not Started`, `-` and
 * `?` all resolved to "assessed" and rendered an amber `0` — a page whose whole
 * argument is that absence is not a no-vote, quietly counting seven absences as
 * no-votes. A list of every way a human might write "we haven't looked yet"
 * cannot be completed, so it must not be the thing correctness rests on.
 *
 * Inverted: only a recognized DECISION counts as assessed. Everything else —
 * blank, unrecognized, a dash, a new status Airtable grows next quarter — is an
 * absence, and an absence makes the figure UNKNOWN rather than zero. The cost
 * of the fail-closed direction is a tile that says "cannot say" when the
 * vocabulary needs extending, which is the failure we want.
 *
 * Live `Status` is single-valued (`Unverified`, 294/294 rows), so nothing on the
 * page changes today.
 */

/** Positive determinations. */
export const LOSAP_QUALIFIED_STATUSES = new Set(["qualified", "yes", "active"]);

/**
 * Negative determinations — someone looked and said no. These COUNT as
 * assessed: they are the "of N" a real zero is measured against, and treating
 * them as absences would be the mirror-image error.
 */
export const LOSAP_NEGATIVE_STATUSES = new Set([
  "not qualified",
  "no",
  "denied",
  "ineligible",
  "disqualified",
  "failed",
]);

/**
 * Normalize a status for matching: lowercase, hyphens/underscores to spaces,
 * whitespace collapsed. So `Not-Qualified`, `NOT QUALIFIED` and `not_qualified`
 * are one value, while `not qualified` still cannot collide with `qualified`.
 */
/**
 * THE single LOSAP status normalizer. Both `losapStatus()` and
 * `isLosapDetermination()` MUST route through this - they used to carry
 * separate copies of the same expression, and nothing tested that the two
 * agreed. Dropping the normalization from one of them passed the entire
 * 65-test suite while reporting a genuinely qualified member to leadership as
 * a confident amber "0 of 1 assessed". The seam between two copies of a rule
 * is exactly where this tile's defects have lived; there is now one copy.
 * (Found by the checker on 2026-08-27 by mutating one copy in isolation.)
 */
export function normalizeLosapStatus(status: unknown): string {
  return asString(status)
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function losapStatus(row: LosapRow): string {
  return normalizeLosapStatus(row.status);
}

/** True only for a recognized decision. Anything unrecognized is an absence. */
export function isLosapDetermination(status: unknown): boolean {
  const s = normalizeLosapStatus(status);
  return LOSAP_QUALIFIED_STATUSES.has(s) || LOSAP_NEGATIVE_STATUSES.has(s);
}

export interface LosapKpi {
  state: "known" | "unknown";
  /** Qualified count. Only meaningful when state is "known". */
  qualified: number;
  /** Rows for the target year carrying any determination at all. */
  assessed: number;
  rowsForYear: number;
  rowsTotal: number;
  /** Latest year present in the feed, for "no rows for 2026" context. */
  latestYear: string | null;
  /** Every reason the figure is unsayable. Empty when state is "known". */
  reasons: string[];
}

/**
 * Classify the LOSAP KPI. Reports EVERY applicable reason rather than the
 * first, because live data trips two at once and naming only one would
 * understate how absent the evidence is.
 */
export function losapKpi(losapRows: LosapRow[], year: number): LosapKpi {
  const yearStr = String(year);
  const forYear = losapRows.filter((r) => asString(r.year) === yearStr);

  let latestYear: string | null = null;
  for (const r of losapRows) {
    const y = asString(r.year);
    if (y && (latestYear === null || y > latestYear)) latestYear = y;
  }

  // DISTINCT MEMBERS, from the same builders the section badge and the
  // per-member engagement award use. Counting rows here gave the KPI row a
  // different number from the badge directly below it on the same page — 3
  // qualified rows across 2 members rendered "3" over a badge reading "2" —
  // and credited rows with no usable join key, which can never reach a member
  // at all. The tile is labeled "members qualified", so members is the unit.
  const qualified = buildLosapQualifiedKeys(losapRows, year).size;
  const assessed = buildLosapAssessedKeys(losapRows, year).size;

  const reasons: string[] = [];
  if (losapRows.length === 0) {
    reasons.push("no LOSAP records on file");
  } else if (forYear.length === 0) {
    reasons.push(
      latestYear
        ? `no ${yearStr} records (latest on file: ${latestYear})`
        : `no ${yearStr} records`,
    );
    // Feed-wide, and only on this branch: when the year is missing entirely,
    // a feed that holds no determination ANYWHERE is a second and larger
    // problem, because fixing the sync alone would not make the number
    // sayable. That is today's live state.
    if (!losapRows.some((r) => isLosapDetermination(r.status))) {
      reasons.push("no record carries a determination (all Unverified)");
    }
  } else if (assessed === 0) {
    // YEAR-SCOPED. This test used to run over the whole feed, so a single
    // historical determination anywhere in the table satisfied it while every
    // current-year row was still Unverified — and the tile printed its own
    // contradiction: "0 of 0 assessed 2026 records qualified". That is not a
    // corner case, it is the most probable next state of this feed: the sync
    // starts delivering current-year rows before anyone has adjudicated them,
    // against a table that by then holds at least one old decision.
    //
    // Two distinct ways to reach zero assessed MEMBERS, named separately
    // because they need different people to fix them: nobody has adjudicated,
    // versus somebody has but the row carries no key we can attribute it to.
    const determinedRows = forYear.filter((r) =>
      isLosapDetermination(r.status),
    ).length;
    reasons.push(
      determinedRows === 0
        ? `no ${yearStr} record carries a determination`
        : `${yearStr} determinations cannot be matched to a member (${determinedRows} record${determinedRows === 1 ? "" : "s"} with no usable name key)`,
    );
  }

  return {
    state: reasons.length === 0 ? "known" : "unknown",
    qualified,
    assessed,
    rowsForYear: forYear.length,
    rowsTotal: losapRows.length,
    latestYear,
    reasons,
  };
}

/** How the LOSAP tile renders. Accent vocabulary is KPICard's own. */
export interface LosapKpiView {
  value: string | number;
  accent: "green" | "amber";
  subtitle: string;
}

/**
 * Render state for the LOSAP tile.
 *
 * GREEN requires a positive qualified count over data that could have produced
 * one. A genuine assessed zero renders `0` in amber — a real finding, and
 * visibly different from `—`, which means we cannot say.
 */
export function losapKpiView(kpi: LosapKpi, year: number): LosapKpiView {
  if (kpi.state === "unknown") {
    return {
      value: "—",
      accent: "amber",
      subtitle: `Cannot report ${year}: ${kpi.reasons.join("; ")}`,
    };
  }
  if (kpi.qualified === 0) {
    return {
      value: 0,
      accent: "amber",
      subtitle: `0 of ${kpi.assessed} assessed ${year} member${kpi.assessed === 1 ? "" : "s"} qualified`,
    };
  }
  return {
    value: kpi.qualified,
    accent: "green",
    subtitle: `members qualified in ${year}`,
  };
}

/** Member ids with an approved driver-training record. */
export function buildDriverApprovedKeys(
  driverRows: DriverTrainingRow[],
): Set<string> {
  const s = new Set<string>();
  for (const row of driverRows) {
    if (asString(row.approval).toLowerCase() !== "approved") continue;
    const key = driverRowKey(row);
    if (key) s.add(key);
  }
  return s;
}

/** Figure 5a — the engagement card list. */
export function computeEngagementMembers(
  activeMembers: MemberRow[],
  physByMember: Map<string, PhysicalsRow>,
  memberAttCounts: Map<string, number>,
  losapQualifiedKeys: Set<string>,
  driverApprovedKeys: Set<string>,
): EngagementMember[] {
  return activeMembers.map((m) => {
    const id = memberIdKey(m);
    const phys = physByMember.get(id);
    const physStatus = classifyPhysical(phys);
    const attendanceCount = memberAttCounts.get(id) ?? 0;
    const losapQual = losapQualifiedKeys.has(memberLosapKey(m));
    const driverApproved = driverApprovedKeys.has(id);

    const score = computeEngagementScore(
      m,
      physStatus,
      attendanceCount,
      losapQual,
      driverApproved,
    );

    const physLabel =
      physStatus === "current"
        ? "Current"
        : physStatus === "expiring"
          ? "Expiring"
          : physStatus === "expired"
            ? "Expired"
            : "None";

    const losapLabel = losapQual ? "Qualified" : "Not Qualified";

    return {
      name: asString(memberName(m), "Unknown"),
      score,
      meetingAttendance: Math.round((Math.min(attendanceCount, 12) / 12) * 100),
      physicalStatus: physLabel,
      losapStatus: losapLabel,
      trainingComplete: !memberStatus(m).toLowerCase().includes("probationary"),
      driverQualified: driverApproved,
    };
  });
}

/**
 * Figure 3 — physical compliance %, the page's headline KPI.
 *
 * Null when there are no active members, which is the honest reading of "no
 * denominator" and renders as "—". It is ALSO what this KPI showed for as long
 * as the status filter was dead: a dash, not a wrong percentage. The dash was
 * the only visible symptom of the defect, and it reads as missing data rather
 * than as a broken filter.
 */
export function computePhysCompliancePct(
  activeMembers: MemberRow[],
  physByMember: Map<string, PhysicalsRow>,
): number | null {
  if (activeMembers.length === 0) return null;
  const compliant = activeMembers.filter((m) => {
    const status = classifyPhysical(physByMember.get(memberIdKey(m)));
    return status === "current" || status === "expiring";
  }).length;
  return Math.round((compliant / activeMembers.length) * 100);
}

/** Figure 4 — active members with zero recorded meeting attendance. */
export function computeZeroAttCount(
  activeMembers: MemberRow[],
  memberAttCounts: Map<string, number>,
): number {
  return activeMembers.filter(
    (m) => (memberAttCounts.get(memberIdKey(m)) ?? 0) === 0,
  ).length;
}

/** Figure 5b — mean engagement score, or null with nobody to average. */
export function computeAvgEngagementScore(
  engagementMembers: EngagementMember[],
): number | null {
  if (engagementMembers.length === 0) return null;
  return Math.round(
    engagementMembers.reduce((sum, m) => sum + m.score, 0) /
      engagementMembers.length,
  );
}

/**
 * Figure 7 — the expired/missing-physical alert list.
 *
 * The one that matters most. While `activeMembers` was empty this returned []
 * unconditionally, and AlertList rendered its empty message: "All active
 * members have current physicals." A compliance surface that is structurally
 * incapable of raising an alert is not a quiet surface, it is a broken one.
 */
export function computePhysAlertItems(
  activeMembers: MemberRow[],
  physByMember: Map<string, PhysicalsRow>,
): AlertItem[] {
  return activeMembers
    .filter((m) => {
      const status = classifyPhysical(physByMember.get(memberIdKey(m)));
      return status === "expired" || status === "none";
    })
    .map((m, idx) => {
      const phys = physByMember.get(memberIdKey(m));
      const status = classifyPhysical(phys);
      const expiresStr = phys?.expires ? ` — expired ${phys.expires}` : "";
      return {
        id: `phys-${idx}`,
        title: asString(memberName(m), "Unknown"),
        subtitle:
          status === "none"
            ? "No physical record on file"
            : `Physical expired${expiresStr}`,
        severity: (status === "expired"
          ? "critical"
          : "warning") as AlertItem["severity"],
        meta: memberStatus(m) || undefined,
      };
    });
}

// ── Per-feed freshness disclosure ─────────────────────────────────────────────
//
// Every figure on this page is only as current as the feed under it, and the
// feeds do NOT move together. Live 2026-08-27: dash_members synced that morning
// (it is on the Worker cron), while physicals, meeting attendance, driver
// training and LOSAP all last synced 2026-08-07 within four minutes of each
// other — a 20-day-old picture behind a page that said nothing about it.
//
// A compliance percentage in front of leadership without an as-of is the same
// defect class as a dead filter: it asserts more than the data supports. So the
// page states the age of each source, and says so loudly when a source is stale
// or unreadable. It does NOT recompute anything to look fresher.
//
// The rule these follow: **absence of evidence is UNKNOWN, never fresh.** No
// rows, or no parseable `synced_at` on any row, resolves to "unknown" and is
// surfaced with the same weight as "stale" — not quietly omitted, and never
// green.

export type FeedFreshnessState = "fresh" | "aging" | "stale" | "unknown";

export interface FeedFreshness {
  label: string;
  /** MAX(synced_at) across the rows, exactly as recorded. Null if unreadable. */
  asOf: string | null;
  /** Whole days since asOf. Null when asOf is null. */
  ageDays: number | null;
  state: FeedFreshnessState;
  rowCount: number;
}

/**
 * Parse a `synced_at` value. D1 holds two shapes across these tables:
 * `2026-08-27T02:00:26.803Z` and `2026-08-07 14:13:09` (space-separated, no
 * zone). Both are UTC; the second is normalized rather than left to the
 * engine's local-time interpretation.
 */
export function parseSyncedAt(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const raw = v.trim();
  if (!raw) return null;
  let iso = raw.includes("T") ? raw : raw.replace(" ", "T");
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Age thresholds in days. Beyond `stale` the feed is treated as not running. */
export const FEED_FRESH_DAYS = 2;
export const FEED_AGING_DAYS = 7;

/**
 * Freshness of one feed, from the `synced_at` its own rows carry.
 *
 * Returns "unknown" — never "fresh" — when there are no rows or when no row
 * carries a parseable timestamp. An empty feed is not a current feed; it is a
 * feed we cannot speak for.
 */
export function feedFreshness(
  label: string,
  rows: ReadonlyArray<{ synced_at?: unknown }>,
  now: number = Date.now(),
): FeedFreshness {
  let newest: number | null = null;
  let newestRaw: string | null = null;
  for (const r of rows) {
    const t = parseSyncedAt(r.synced_at);
    if (t !== null && (newest === null || t > newest)) {
      newest = t;
      newestRaw = String(r.synced_at);
    }
  }
  if (newest === null) {
    return {
      label,
      asOf: null,
      ageDays: null,
      state: "unknown",
      rowCount: rows.length,
    };
  }
  const ageDays = Math.floor((now - newest) / 86_400_000);
  const state: FeedFreshnessState =
    ageDays <= FEED_FRESH_DAYS
      ? "fresh"
      : ageDays <= FEED_AGING_DAYS
        ? "aging"
        : "stale";
  return { label, asOf: newestRaw, ageDays, state, rowCount: rows.length };
}

/** The date part of an as-of stamp, for display. "—" when unreadable. */
export function asOfLabel(f: FeedFreshness): string {
  if (!f.asOf) return "—";
  const t = parseSyncedAt(f.asOf);
  if (t === null) return "—";
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * The feeds needing disclosure: anything not fresh, worst first. Empty means
 * every source is current and the page can speak plainly.
 */
export function staleFeeds(feeds: FeedFreshness[]): FeedFreshness[] {
  const rank: Record<FeedFreshnessState, number> = {
    unknown: 0,
    stale: 1,
    aging: 2,
    fresh: 3,
  };
  return feeds
    .filter((f) => f.state !== "fresh")
    .sort((a, b) => rank[a.state] - rank[b.state]);
}

/** One-line disclosure naming each non-fresh source and its age. */
export function freshnessNotice(feeds: FeedFreshness[]): string | null {
  const bad = staleFeeds(feeds);
  if (bad.length === 0) return null;
  const parts = bad.map((f) =>
    f.state === "unknown"
      ? `${f.label} (age unknown)`
      : `${f.label} ${f.ageDays}d`,
  );
  return `Figures below are as old as their source: ${parts.join(" · ")}.`;
}

const FEED_STATE_CLASS: Record<FeedFreshnessState, string> = {
  fresh: "text-status-green",
  aging: "text-status-amber",
  stale: "text-status-red",
  unknown: "text-status-red",
};

/** Compact per-source as-of strip. Always rendered — silence is not health. */
function FreshnessDisclosure({ feeds }: { feeds: FeedFreshness[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-theme-muted">
      <span className="font-semibold uppercase tracking-wider">Data as of</span>
      {feeds.map((f) => (
        <span key={f.label} className="flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              f.state === "fresh"
                ? "bg-status-green"
                : f.state === "aging"
                  ? "bg-status-amber"
                  : "bg-status-red"
            }`}
          />
          <span>{f.label}</span>
          <span className={`font-medium ${FEED_STATE_CLASS[f.state]}`}>
            {asOfLabel(f)}
            {f.state === "unknown"
              ? " (unknown)"
              : f.ageDays !== null && f.ageDays > FEED_FRESH_DAYS
                ? ` (${f.ageDays}d old)`
                : ""}
          </span>
        </span>
      ))}
    </div>
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function BarTooltip({
  active,
  payload,
  labelKey = "label",
  countLabel = "members",
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown>; value: number }>;
  labelKey?: string;
  countLabel?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload[0];
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-theme-primary">
        {String(entry.payload[labelKey] ?? "")}
      </p>
      <p className="text-theme-secondary mt-0.5">
        <span className="font-bold text-brand-adaptive">
          {entry.value.toLocaleString()}
        </span>{" "}
        {countLabel}
      </p>
    </div>
  );
}

function PieTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload[0];
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-theme-primary">{entry.name}</p>
      <p className="text-theme-secondary mt-0.5">
        <span className="font-bold" style={{ color: entry.payload.fill }}>
          {entry.value.toLocaleString()}
        </span>{" "}
        members
      </p>
    </div>
  );
}

// ── Section: Physical Pie Chart ───────────────────────────────────────────────

function PhysicalComplianceChart({
  slices,
  isLoading,
}: {
  slices: PhysPieSlice[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div
        className="skeleton rounded-full mx-auto"
        style={{ width: 200, height: 200 }}
      />
    );
  }
  if (slices.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-theme-muted">
        No physical data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={slices}
          cx="50%"
          cy="50%"
          innerRadius={70}
          outerRadius={110}
          paddingAngle={3}
          dataKey="value"
        >
          {slices.map((entry, idx) => (
            <Cell key={`phys-${idx}`} fill={entry.fill} stroke="transparent" />
          ))}
        </Pie>
        <Tooltip content={<PieTooltip />} />
        <Legend
          iconType="circle"
          iconSize={9}
          formatter={(value) => (
            <span className="text-xs text-theme-secondary">{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Section: LOSAP Bar Chart ──────────────────────────────────────────────────

function LosapYearChart({
  rows,
  isLoading,
}: {
  rows: LosapYearRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 220 }} />;
  }
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-theme-muted">
        No LOSAP data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={rows}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          content={<BarTooltip labelKey="year" countLabel="qualified" />}
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar dataKey="qualified" radius={[4, 4, 0, 0]} maxBarSize={48}>
          {rows.map((row, idx) => (
            <Cell
              key={`losap-${idx}`}
              fill={row.isCurrent ? "#f59e0b" : DCVFD_ACCENT}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Section: Attendance Histogram ─────────────────────────────────────────────

function AttendanceHistogram({
  buckets,
  isLoading,
}: {
  buckets: AttendanceBucket[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 220 }} />;
  }

  const hasData = buckets.some((b) => b.count > 0);
  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-theme-muted">
        No attendance data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={buckets}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        barCategoryGap="15%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          content={<BarTooltip labelKey="label" countLabel="members" />}
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={64}>
          {buckets.map((b, idx) => (
            <Cell
              key={`att-${idx}`}
              fill={b.isGhost ? GHOST_COLOR : DCVFD_ACCENT}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Section: Driver KPI Cards ─────────────────────────────────────────────────

function DriverStatusCards({
  rows,
  isLoading,
}: {
  rows: DriverTrainingRow[];
  isLoading: boolean;
}) {
  const counts = useMemo(() => {
    const approved = rows.filter(
      (r) => asString(r.approval).toLowerCase() === "approved",
    ).length;
    const denied = rows.filter(
      (r) => asString(r.approval).toLowerCase() === "denied",
    ).length;
    const pending = rows.filter((r) => {
      const a = asString(r.approval).toLowerCase();
      return !a || (a !== "approved" && a !== "denied");
    }).length;
    return { approved, denied, pending };
  }, [rows]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <KPICard
        label="Approved"
        value={isLoading ? "—" : counts.approved}
        subtitle="driver-qualified members"
        accent="green"
        isLoading={isLoading}
      />
      <KPICard
        label="Pending / Unknown"
        value={isLoading ? "—" : counts.pending}
        subtitle="awaiting or no status"
        accent="amber"
        isLoading={isLoading}
      />
      <KPICard
        label="Denied"
        value={isLoading ? "—" : counts.denied}
        subtitle="not approved to drive"
        accent="red"
        isLoading={isLoading}
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MemberEngagement() {
  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const {
    data: aggData,
    isLoading: _aggLoading,
    lastUpdatedAt: aggUpdated,
  } = useAggregates();
  // Limit 5,000 = the API hard maximum. Live: 1,895 rows in dash_members
  // (2026-08-27), 2.6x headroom. This call site asked for 1,000 until
  // 2026-08-27, so 895 members (47%) never reached the page and nothing on the
  // wire said so — every count below was computed over 53% of the membership.
  const { data: membersData, isLoading: membersLoading } = useMembers({
    limit: MEMBERS_PAGE_LIMIT,
  });
  const { data: physicalsData, isLoading: physLoading } = usePhysicals();
  const { data: attendanceData, isLoading: attLoading } =
    useMeetingAttendance();
  const { data: atRiskData, isLoading: atRiskLoading } = useAtRisk();
  const { data: losapData, isLoading: losapLoading } = useLosap();
  const { data: driverData, isLoading: driverLoading } = useDriverTraining();

  // ── Raw arrays ────────────────────────────────────────────────────────────
  // Each is memoized on its own payload rather than written as a bare
  // `data?.x ?? []`. The `?? []` allocates a NEW empty array on every render,
  // so any useMemo depending on it re-runs every render — which is what the
  // exhaustive-deps warnings on this page were reporting. Every derived figure
  // below hangs off these, so the churn is not free.
  const allMembers = useMemo<MemberRow[]>(
    () => membersData?.members ?? [],
    [membersData],
  );
  const allPhysRows = useMemo<PhysicalsRow[]>(
    () => physicalsData?.data ?? [],
    [physicalsData],
  );
  const allAttRows = useMemo<MeetingAttendanceRow[]>(
    () => attendanceData?.data ?? [],
    [attendanceData],
  );
  const atRiskRows = useMemo<AtRiskRow[]>(
    () => atRiskData?.data ?? [],
    [atRiskData],
  );
  const losapRows = useMemo<LosapRow[]>(
    () => losapData?.data ?? [],
    [losapData],
  );
  const driverRows = useMemo<DriverTrainingRow[]>(
    () => driverData?.data ?? [],
    [driverData],
  );

  // ── Date range filter ─────────────────────────────────────────────────────
  //
  // Two dead fallback keys removed here, surfaced by closing PhysicalsRow and
  // MeetingAttendanceRow: `exam_date` is not a column on dash_physicals, and
  // `date` is not a column on dash_meeting_attendance. Both read undefined on
  // every row, so behavior is unchanged — but the `??` chains read as though
  // there were a second source of truth, and there never was.
  const physRows = useMemo<PhysicalsRow[]>(
    () => filterPhysRowsByRange(allPhysRows, sinceDate),
    [allPhysRows, sinceDate],
  );

  const attRows = useMemo<MeetingAttendanceRow[]>(
    () => filterAttRowsByRange(allAttRows, sinceDate),
    [allAttRows, sinceDate],
  );

  const isAnyLoading =
    membersLoading ||
    physLoading ||
    attLoading ||
    losapLoading ||
    driverLoading;

  // ── Active members ────────────────────────────────────────────────────────
  const activeMembers = useMemo(
    () => selectActiveMembers(allMembers),
    [allMembers],
  );

  // ── Physical lookup: member record id → most-recent row ──────────────────
  const physByMember = useMemo<Map<string, PhysicalsRow>>(
    () => buildPhysByMember(physRows),
    [physRows],
  );

  // ── Attendance count per member (this year) ───────────────────────────────
  const memberAttCounts = useMemo<Map<string, number>>(
    () => buildMemberAttCounts(attRows),
    [attRows],
  );

  // ── LOSAP qualified members this year ─────────────────────────────────────
  const losapQualifiedKeys = useMemo<Set<string>>(
    () => buildLosapQualifiedKeys(losapRows, CURRENT_YEAR),
    [losapRows],
  );

  // ── Driver approved lookup ────────────────────────────────────────────────
  const driverApprovedKeys = useMemo<Set<string>>(
    () => buildDriverApprovedKeys(driverRows),
    [driverRows],
  );

  // ── Engagement members (scored + sorted) ─────────────────────────────────
  const engagementMembers = useMemo<EngagementMember[]>(
    () =>
      computeEngagementMembers(
        activeMembers,
        physByMember,
        memberAttCounts,
        losapQualifiedKeys,
        driverApprovedKeys,
      ),
    [
      activeMembers,
      physByMember,
      memberAttCounts,
      losapQualifiedKeys,
      driverApprovedKeys,
    ],
  );

  // ── KPI: Physical compliance % ────────────────────────────────────────────
  const physCompliancePct = useMemo(
    () => computePhysCompliancePct(activeMembers, physByMember),
    [activeMembers, physByMember],
  );

  // ── KPI: LOSAP qualified this year ────────────────────────────────────────
  // losapQualCount still drives the per-member award and the section badge; the
  // TILE goes through losapKpi() because an empty set here does not mean zero
  // qualified, it means the feed cannot answer. See losapKpi().
  const losapQualCount = losapQualifiedKeys.size;
  const losapKpiState = useMemo(
    () => losapKpi(losapRows, CURRENT_YEAR),
    [losapRows],
  );
  const losapTile = useMemo(
    () => losapKpiView(losapKpiState, CURRENT_YEAR),
    [losapKpiState],
  );
  /** True when the feed carries no determination at all, for any year. */
  const losapUnknown = losapKpiState.reasons.some((r) =>
    r.includes("determination"),
  );

  // ── KPI: Zero-attendance members ─────────────────────────────────────────
  const zeroAttCount = useMemo(
    () => computeZeroAttCount(activeMembers, memberAttCounts),
    [activeMembers, memberAttCounts],
  );

  // ── KPI: Average engagement score ────────────────────────────────────────
  const avgEngagementScore = useMemo(
    () => computeAvgEngagementScore(engagementMembers),
    [engagementMembers],
  );

  // ── Physical pie slices ───────────────────────────────────────────────────
  const physPieSlices = useMemo(
    () => computePhysicalPie(activeMembers, physByMember),
    [activeMembers, physByMember],
  );

  // ── Expired/missing physical alert items ─────────────────────────────────
  const physAlertItems = useMemo<AlertItem[]>(
    () => computePhysAlertItems(activeMembers, physByMember),
    [activeMembers, physByMember],
  );

  // ── Per-feed freshness ────────────────────────────────────────────────────
  // Computed from the UNFILTERED rows: the date-range toggle must not be able
  // to make a source look fresher than it is.
  const feeds = useMemo<FeedFreshness[]>(
    () => [
      feedFreshness("Members", allMembers),
      feedFreshness("Physicals", allPhysRows),
      feedFreshness("Attendance", allAttRows),
      feedFreshness("LOSAP", losapRows),
      feedFreshness("Driver training", driverRows),
    ],
    [allMembers, allPhysRows, allAttRows, losapRows, driverRows],
  );
  const freshnessWarning = useMemo(() => freshnessNotice(feeds), [feeds]);
  const physFeed = feeds[1];

  // ── At-risk alert items (from useAtRisk) ─────────────────────────────────
  // (only used for count; engagement card handles detailed display)
  const atRiskAlertItems = useMemo<AlertItem[]>(() => {
    return atRiskRows
      .filter((r) => {
        const att =
          r.t_2024_co_meeting_attendance ?? r.meeting_attendance_ytd ?? null;
        return att === 0 || att === null;
      })
      .map((r, idx) => ({
        id: `atrisk-${idx}`,
        title: asString(r.full_name, "Unknown"),
        subtitle: asString(r.status_new) || undefined,
        severity: "warning" as AlertItem["severity"],
        meta: "No attendance",
      }));
  }, [atRiskRows]);

  // ── LOSAP by year ─────────────────────────────────────────────────────────
  const losapByYear = useMemo(() => computeLosapByYear(losapRows), [losapRows]);

  // ── Attendance histogram ──────────────────────────────────────────────────
  const attendanceBuckets = useMemo(
    () => computeAttendanceHistogram(memberAttCounts),
    [memberAttCounts],
  );

  // ── Refresh timestamp ─────────────────────────────────────────────────────
  const secAgo = aggUpdated
    ? Math.round((Date.now() - aggUpdated.getTime()) / 1000)
    : null;
  const _ = aggData; // keep aggLoading reference quiet

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Member Engagement &amp; Compliance
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Per-member health check: physical compliance, LOSAP, meeting
            attendance, and engagement scores.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="FORGE data" variant="blue" />
          {secAgo !== null && (
            <div className="flex items-center gap-1.5 text-xs text-theme-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
              <span>Updated {secAgo < 5 ? "just now" : `${secAgo}s ago`}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Row-completeness notice ───────────────────────────────────
          Every figure on this page is computed over the member rows that
          loaded. Until 2026-08-27 this page asked for 1,000 of dash_members'
          1,895 rows and said nothing about the other 895 — and because the
          query is ORDER BY rowid, the missing rows were an insert-order slice
          of the roster, not a sample of it. Silence here now means the page
          PROVED it holds every row; it never means the count was absent. */}
      <TruncationNotice
        response={membersData}
        label="member"
        hint="Physical compliance, LOSAP and attendance figures below are computed from the member rows that loaded."
      />

      {/* ── Date Range Toggle ─────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── Per-source freshness ──────────────────────────────────────── */}
      {/* Always rendered, not only when something is wrong: a disclosure that
          appears only on failure teaches the reader that its absence means
          "current", which is the assumption that let a 20-day-old compliance
          figure read as today's. */}
      {!isAnyLoading && (
        <div className="space-y-3">
          <FreshnessDisclosure feeds={feeds} />
          {freshnessWarning && (
            <Banner variant="warn" title="Some sources are not current">
              {freshnessWarning} These are point-in-time figures from the last
              successful sync, not live counts.
            </Banner>
          )}
        </div>
      )}

      {/* ── KPI Row ──────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Member Health Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="Physical Compliance"
            value={
              physLoading || membersLoading
                ? "—"
                : physCompliancePct !== null
                  ? `${physCompliancePct}%`
                  : "—"
            }
            subtitle={
              physFeed.state === "fresh"
                ? `of ${activeMembers.length} active members`
                : `of ${activeMembers.length} active — physicals as of ${asOfLabel(physFeed)}`
            }
            accent={
              physCompliancePct === null
                ? "neutral"
                : physCompliancePct >= 80
                  ? "green"
                  : physCompliancePct >= 60
                    ? "amber"
                    : "red"
            }
            isLoading={physLoading || membersLoading}
            trend={
              !physLoading && !membersLoading && physCompliancePct !== null
                ? {
                    direction: physCompliancePct >= 80 ? "up" : "down",
                    label: physCompliancePct >= 80 ? "On target" : "Below 80%",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
          <KPICard
            label="LOSAP Qualified"
            value={losapLoading ? "—" : losapTile.value}
            subtitle={losapTile.subtitle}
            accent={losapLoading ? "neutral" : losapTile.accent}
            isLoading={losapLoading}
            trend={
              // No trend on an unsayable figure. An "up / N qualified" chip
              // over a set that is empty because nobody has assessed anybody is
              // the same false-green in a smaller font.
              !losapLoading && losapTile.accent === "green"
                ? {
                    direction: "up",
                    label: `${losapTile.value} qualified`,
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
          <KPICard
            label="Zero-Attendance Members"
            value={attLoading || membersLoading ? "—" : zeroAttCount}
            subtitle={
              zeroAttCount === 0
                ? "All members attended"
                : "no meetings attended this year"
            }
            accent={
              zeroAttCount > 5 ? "red" : zeroAttCount > 0 ? "amber" : "green"
            }
            isLoading={attLoading || membersLoading}
            trend={
              !attLoading && !membersLoading
                ? {
                    direction: zeroAttCount === 0 ? "down" : "up",
                    label:
                      zeroAttCount === 0
                        ? "None ghosted"
                        : `${zeroAttCount} ghosts`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />
          <KPICard
            label="Avg Engagement Score"
            value={
              isAnyLoading
                ? "—"
                : avgEngagementScore !== null
                  ? avgEngagementScore
                  : "—"
            }
            subtitle="out of 100 — target: ≥70"
            accent={
              avgEngagementScore === null
                ? "neutral"
                : avgEngagementScore > 70
                  ? "green"
                  : avgEngagementScore > 40
                    ? "amber"
                    : "red"
            }
            isLoading={isAnyLoading}
            trend={
              !isAnyLoading && avgEngagementScore !== null
                ? {
                    direction: avgEngagementScore > 70 ? "up" : "down",
                    label: avgEngagementScore > 70 ? "Healthy" : "Needs focus",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Engagement Scores ─────────────────────────────── */}
      <section>
        <ChartCard
          title="Engagement Scores — All Active Members"
          subtitle="Composite score (0–100): meeting attendance, physical status, LOSAP, training, driver qualification. Bottom = retention risks."
          headerRight={
            isAnyLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : engagementMembers.length > 0 ? (
              <StatusBadge
                label={`${engagementMembers.length} active members`}
                variant="muted"
              />
            ) : null
          }
          minHeight={0}
        >
          <EngagementScoreCard
            members={engagementMembers}
            isLoading={isAnyLoading}
            maxVisible={20}
          />
        </ChartCard>
      </section>

      {/* ── Section 2: Physical Exam Compliance ──────────────────────── */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Physical Exam Compliance"
            subtitle="Status breakdown for all active members — expiring = <90 days"
            headerRight={
              physLoading ? (
                <span className="text-xs text-theme-muted animate-pulse">
                  Loading...
                </span>
              ) : (
                <StatusBadge
                  label={`${activeMembers.length} active`}
                  variant="muted"
                />
              )
            }
            minHeight={260}
          >
            <PhysicalComplianceChart
              slices={physPieSlices}
              isLoading={physLoading || membersLoading}
            />
          </ChartCard>

          <AlertList
            title="Expired or Missing Physicals"
            items={physAlertItems}
            emptyMessage={
              physLoading || membersLoading
                ? "Loading physical data..."
                : "All active members have current physicals"
            }
            isLoading={physLoading || membersLoading}
            maxVisible={8}
          />
        </div>
      </section>

      {/* ── Section 3: LOSAP Tracking ─────────────────────────────────── */}
      <section>
        <ChartCard
          title="LOSAP Qualification by Year"
          subtitle={
            // The chart plots the QUALIFIED count per year. When no record in
            // the feed carries a determination, every bar is 0 — which reads as
            // "nobody qualified" and is the same false zero the KPI tile above
            // now refuses to assert. Say so here rather than let the chart
            // imply it.
            losapUnknown
              ? `No qualification determinations on file — every bar below is an absence of assessment, not a count of zero`
              : `Members qualified per year — ${CURRENT_YEAR} highlighted in amber`
          }
          headerRight={
            losapLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : losapUnknown ? (
              <StatusBadge label="Unverified" variant="red" />
            ) : losapQualCount > 0 ? (
              <StatusBadge
                label={`${losapQualCount} this year`}
                variant="yellow"
              />
            ) : null
          }
          minHeight={220}
        >
          <LosapYearChart rows={losapByYear} isLoading={losapLoading} />
        </ChartCard>
      </section>

      {/* ── Section 4: Meeting Attendance Distribution ────────────────── */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Meeting Attendance Distribution"
            subtitle='How many meetings each active member has attended this year. Zero-attendance "ghosts" highlighted in red.'
            headerRight={
              attLoading ? (
                <span className="text-xs text-theme-muted animate-pulse">
                  Loading...
                </span>
              ) : zeroAttCount > 0 ? (
                <StatusBadge label={`${zeroAttCount} ghosts`} variant="red" />
              ) : null
            }
            minHeight={220}
          >
            <AttendanceHistogram
              buckets={attendanceBuckets}
              isLoading={attLoading || membersLoading}
            />
          </ChartCard>

          <AlertList
            title="Zero-Attendance Members"
            items={atRiskAlertItems}
            emptyMessage={
              attLoading || atRiskLoading
                ? "Loading attendance data..."
                : "No zero-attendance members this year"
            }
            isLoading={attLoading || atRiskLoading}
            maxVisible={8}
          />
        </div>
      </section>

      {/* ── Section 5: Driver Qualification Status ───────────────────── */}
      <section>
        <ChartCard
          title="Driver Qualification Status"
          subtitle="Approval status for all driver training applicants"
          headerRight={
            driverLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : driverRows.length > 0 ? (
              <StatusBadge
                label={`${driverRows.length} records`}
                variant="muted"
              />
            ) : null
          }
          minHeight={0}
        >
          <DriverStatusCards rows={driverRows} isLoading={driverLoading} />
        </ChartCard>
      </section>
    </div>
  );
}
